import { useCallback, useEffect, useState } from 'react'
import { createLocalStorageManager, type ColorMode } from '@chakra-ui/react'
import {
  ADMIN_MERCHANT_FILTER_STORAGE_KEY,
  ADMIN_ORDER_FILTER_STORAGE_KEY,
  ADMIN_CURRENT_ENV_STORAGE_KEY,
  NETWORK_ENVIRONMENTS,
  withNetworkStorageKey,
} from '@/utils/storageKeys'

export type PrivacyPreferences = {
  version: number
  timestamp: string
  region: 'EU' | 'UK' | 'US' | 'OTHER'
  functional: boolean
  analytics: boolean
  marketing: boolean
  gpc: boolean
}

const PRIVACY_PREFERENCES_KEY = 'admin_portal_privacy_prefs_v1'
const PRIVACY_PREFERENCES_EVENT = 'admin-portal-privacy-preferences-change'
export const PRIVACY_PREFERENCES_BAR_EVENT =
  'admin-portal-privacy-preferences-bar-open'

const DEFAULT_PRIVACY_PREFERENCES: PrivacyPreferences = {
  version: 1,
  timestamp: '',
  region: 'OTHER',
  functional: false,
  analytics: false,
  marketing: false,
  gpc: false,
}

const NETWORK_SCOPED_KEYS = [
  'admin_portal_language',
  'admin_portal_color_mode',
  'admin_portal_sidebar_collapsed',
  ADMIN_ORDER_FILTER_STORAGE_KEY,
  ADMIN_MERCHANT_FILTER_STORAGE_KEY,
  'admin_portal_system_config_tab',
  'admin_portal_admin_users_tab',
  'admin_portal_payment_management_tab',
  'admin_portal_rescue_tab',
  'admin_portal_monitoring_tab',
  'admin_portal_support_tab',
  'admin_portal_contract_registry_tab',
]

const FUNCTIONAL_STORAGE_KEYS = [
  ...NETWORK_ENVIRONMENTS.flatMap(mode =>
    NETWORK_SCOPED_KEYS.map(key => withNetworkStorageKey(key, mode))
  ),
  ADMIN_CURRENT_ENV_STORAGE_KEY,
]

const detectGpc = (): boolean => {
  if (typeof navigator === 'undefined') {
    return false
  }
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean }
  return Boolean(nav.globalPrivacyControl)
}

const normalizePrivacyPreferences = (
  value: Partial<PrivacyPreferences> | null,
  gpc: boolean
): PrivacyPreferences => {
  const next: PrivacyPreferences = {
    ...DEFAULT_PRIVACY_PREFERENCES,
    ...value,
    version: DEFAULT_PRIVACY_PREFERENCES.version,
    gpc,
  }
  if (gpc) {
    next.analytics = false
  }
  return next
}

export const getPrivacyPreferences = (): PrivacyPreferences => {
  const gpc = detectGpc()
  if (typeof window === 'undefined') {
    return normalizePrivacyPreferences(null, gpc)
  }

  try {
    const raw = window.localStorage.getItem(PRIVACY_PREFERENCES_KEY)
    if (!raw) {
      return normalizePrivacyPreferences(null, gpc)
    }
    const parsed = JSON.parse(raw) as Partial<PrivacyPreferences>
    return normalizePrivacyPreferences(parsed, gpc)
  } catch {
    return normalizePrivacyPreferences(null, gpc)
  }
}

export const setPrivacyPreferences = (next: PrivacyPreferences): PrivacyPreferences => {
  if (typeof window === 'undefined') {
    return next
  }

  try {
    window.localStorage.setItem(PRIVACY_PREFERENCES_KEY, JSON.stringify(next))
    window.dispatchEvent(
      new CustomEvent(PRIVACY_PREFERENCES_EVENT, { detail: next })
    )
  } catch {
    // Ignore storage errors
  }
  return next
}

export const openPrivacyPreferencesBar = (): void => {
  if (typeof window === 'undefined') {
    return
  }
  window.dispatchEvent(new Event(PRIVACY_PREFERENCES_BAR_EVENT))
}

export const updatePrivacyPreferences = (
  partial: Partial<PrivacyPreferences>
): PrivacyPreferences => {
  const current = getPrivacyPreferences()
  const next: PrivacyPreferences = {
    ...current,
    ...partial,
    version: DEFAULT_PRIVACY_PREFERENCES.version,
    timestamp: new Date().toISOString(),
    gpc: detectGpc(),
  }
  if (next.gpc) {
    next.analytics = false
  }
  return setPrivacyPreferences(next)
}

export const shouldPersistFunctionalPreferences = (): boolean => {
  if (typeof window === 'undefined') {
    return false
  }
  return getPrivacyPreferences().functional
}

export const shouldAllowAnalytics = (): boolean => {
  if (typeof window === 'undefined') {
    return false
  }
  const prefs = getPrivacyPreferences()
  return prefs.analytics && !prefs.gpc
}

export const readFunctionalStorage = (key: string): string | null => {
  if (!shouldPersistFunctionalPreferences()) {
    return null
  }
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

export const writeFunctionalStorage = (key: string, value: string): void => {
  if (!shouldPersistFunctionalPreferences()) {
    return
  }
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // Ignore storage errors
  }
}

export const removeFunctionalStorage = (key: string): void => {
  if (typeof window === 'undefined') {
    return
  }
  try {
    window.localStorage.removeItem(key)
  } catch {
    // Ignore storage errors
  }
}

export const clearNetworkScopedStorage = (): void => {
  if (typeof window === 'undefined') {
    return
  }
  NETWORK_ENVIRONMENTS.forEach(mode => {
    NETWORK_SCOPED_KEYS.forEach(key => {
      removeFunctionalStorage(withNetworkStorageKey(key, mode))
    })
  })
}

export const clearFunctionalPreferences = (): void => {
  if (typeof window === 'undefined') {
    return
  }
  FUNCTIONAL_STORAGE_KEYS.forEach(key => {
    removeFunctionalStorage(key)
  })
}

export const usePrivacyPreferences = () => {
  const [preferences, setPreferencesState] = useState<PrivacyPreferences>(() =>
    getPrivacyPreferences()
  )

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const handleCustomEvent = (event: Event) => {
      const customEvent = event as CustomEvent<PrivacyPreferences>
      if (customEvent.detail) {
        setPreferencesState(customEvent.detail)
      } else {
        setPreferencesState(getPrivacyPreferences())
      }
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key === PRIVACY_PREFERENCES_KEY) {
        setPreferencesState(getPrivacyPreferences())
      }
    }

    window.addEventListener(
      PRIVACY_PREFERENCES_EVENT,
      handleCustomEvent as EventListener
    )
    window.addEventListener('storage', handleStorage)

    return () => {
      window.removeEventListener(
        PRIVACY_PREFERENCES_EVENT,
        handleCustomEvent as EventListener
      )
      window.removeEventListener('storage', handleStorage)
    }
  }, [])

  const setPrivacy = useCallback((partial: Partial<PrivacyPreferences>) => {
    setPreferencesState(updatePrivacyPreferences(partial))
  }, [])

  const setFunctionalEnabled = useCallback(
    (enabled: boolean) => {
      setPrivacy({ functional: enabled })
      if (!enabled) {
        clearFunctionalPreferences()
      }
    },
    [setPrivacy]
  )

  const setAnalyticsEnabled = useCallback(
    (enabled: boolean) => {
      setPrivacy({ analytics: enabled })
    },
    [setPrivacy]
  )

  return {
    preferences,
    setPrivacyPreferences: setPrivacy,
    setFunctionalEnabled,
    setAnalyticsEnabled,
  }
}

export const createConsentAwareColorModeManager = (
  storageKey: string
): ReturnType<typeof createLocalStorageManager> => {
  const baseManager = createLocalStorageManager(storageKey)
  let memoryValue: ColorMode | undefined

  const resolveColorMode = (value: ColorMode | 'system'): ColorMode => {
    if (value !== 'system') {
      return value
    }
    if (typeof window === 'undefined' || !window.matchMedia) {
      return 'light'
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }

  return {
    type: baseManager.type,
    get: (initialValue: ColorMode | 'system' = 'light') => {
      const resolvedInitialValue = resolveColorMode(initialValue)
      if (!shouldPersistFunctionalPreferences()) {
        return memoryValue ?? resolvedInitialValue
      }
      return baseManager.get(resolvedInitialValue)
    },
    set: (value: ColorMode | 'system') => {
      if (!shouldPersistFunctionalPreferences()) {
        memoryValue = resolveColorMode(value)
        return
      }
      baseManager.set(resolveColorMode(value))
    },
  }
}
